// List order, grouping and the reading session. Both clients follow these rules;
// Android mirrors them in Kotlin and runs the same vectors.
import type { ArticleRow, Filter, Grouping, Order } from "./types";
import { readDayGroup, timeGroup } from "./time";

type Row = Pick<ArticleRow, "id" | "feedId" | "publishedAt" | "isRead" | "isSaved" | "readAt">;

/** Filter rows. `keep` holds ids read during this session: they stay visible under Unread. */
export function applyFilter<T extends Row>(rows: readonly T[], filter: Filter, keep: ReadonlySet<string> = new Set()): T[] {
  if (filter === "all") return rows.slice();
  if (filter === "saved") return rows.filter((r) => r.isSaved);
  return rows.filter((r) => !r.isRead || keep.has(r.id));
}

const byTime = (order: Order) => (a: Row, b: Row) =>
  (order === "oldest" ? a.publishedAt - b.publishedAt : b.publishedAt - a.publishedAt) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);

/**
 * Display order. By time: publication order. By source: sources ordered by their
 * first item in the chosen order (oldest-first puts the source with the oldest
 * item first), items inside a source in the same order.
 */
export function orderRows<T extends Row>(rows: readonly T[], order: Order, grouping: Grouping): T[] {
  const sorted = rows.slice().sort(byTime(order));
  if (grouping === "time") return sorted;
  const firstIdx = new Map<string, number>();
  sorted.forEach((r, i) => { if (!firstIdx.has(r.feedId)) firstIdx.set(r.feedId, i); });
  return sorted
    .map((r, i) => ({ r, i }))
    .sort((x, y) => firstIdx.get(x.r.feedId)! - firstIdx.get(y.r.feedId)! || x.i - y.i)
    .map((x) => x.r);
}

/** Recently read: newest read first. */
export const orderRecent = <T extends Row>(rows: readonly T[]): T[] =>
  rows.filter((r) => r.readAt).slice().sort((a, b) => b.readAt! - a.readAt! || (a.id < b.id ? -1 : 1));

export type SessionChange = "hydrate" | "page" | "live";

/** A page extends the captured order; live arrivals join only beyond its current frontier. */
export function extendSession(session: readonly string[], shown: readonly string[], kind: SessionChange): string[] {
  if (kind === "hydrate" && session.length === 1 && shown.includes(session[0]!)) return [...new Set(shown)];
  const end = shown.indexOf(session.at(-1) ?? "");
  const more = kind === "page" ? shown : end < 0 ? [] : shown.slice(end + 1);
  return [...new Set([...session, ...more])];
}

export interface Group { key: string; label: string; feedId?: string; ids: string[] }

/** Consecutive groups over already-ordered rows. */
export function groupRows(
  rows: readonly Row[], how: Grouping | "recent", now: number, tzOffsetMin: number,
  feedTitle: (feedId: string) => string = (f) => f,
): Group[] {
  const out: Group[] = [];
  for (const r of rows) {
    const g = how === "source" ? { key: `f:${r.feedId}`, label: feedTitle(r.feedId), feedId: r.feedId }
      : how === "recent" ? readDayGroup(r.readAt ?? now, now, tzOffsetMin)
      : timeGroup(r.publishedAt, now, tzOffsetMin);
    const last = out[out.length - 1];
    if (last && last.key === g.key) last.ids.push(r.id);
    else out.push({ ...g, ids: [r.id] });
  }
  return out;
}
